import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "Layout/slice";
import { trackEvent } from "utils/tracking";
import { TRADE_SIDE } from "utils/constants";
import { useAllStrategyHoldings } from "features/Strategy/hook/useAllStrategyHoldings";
import {
  useBrokerAccounts,
  useTradingConnection,
  useTradingAllocation,
  usePostTradingConnection,
} from "./index";

export const useInvestmentOrder = (strategyId) => {
  const dispatch = useDispatch();

  // Local state
  const [activeTab, setActiveTab] = useState(TRADE_SIDE.BUY);
  const [step, setStep] = useState(1);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [investmentAmount, setInvestmentAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [strategyData, setStrategyData] = useState(null);
  const [tradingConnectionData, setTradingConnectionData] = useState(null);
  const [isLoadingCash, setIsLoadingCash] = useState(false);
  const [isHoldingsModalOpen, setIsHoldingsModalOpen] = useState(false);

  // Fetch strategy holdings data
  const {
    data: holdingsData = [],
    isLoading: isLoadingHoldings,
    isError: isHoldingsError,
  } = useAllStrategyHoldings(strategyId);

  // Fetch broker accounts using custom hook
  const {
    data: portfolios = [],
    isLoading: loading,
    isError,
  } = useBrokerAccounts(
    strategyId,
    (response) => {
      // Extract strategy data from response
      if (response && Array.isArray(response)) {
        const firstItem = response[0];
        if (firstItem && firstItem.strategy_name) {
          setStrategyData({
            strategy_name: firstItem.strategy_name,
            logo_url: firstItem.logo_url,
            strategy_id: firstItem.strategy_id,
          });
        }
      }
    },
    () => {
      dispatch(
        toast({
          type: "error",
          body: "Failed to load broker accounts. Please try again.",
          visible: true,
        })
      );
    }
  );

  // Create trading connection mutation
  const createTradingConnectionMutation = useTradingConnection(
    (response) => {
      // Store the trading connection data
      if (response) {
        setTradingConnectionData(response);
      }
      setIsLoadingCash(false);
      setIsSubmitting(false);
    },
    (error) => {
      // Clear trading connection data on error
      setTradingConnectionData(null);

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create trading connection. Please try again.";

      dispatch(
        toast({
          type: "error",
          body: errorMessage,
          visible: true,
        })
      );
      setIsLoadingCash(false);
      setIsSubmitting(false);
    }
  );

  // Post trading connection mutation (for not connected accounts)
  const postTradingConnectionMutation = usePostTradingConnection(
    (response) => {
      // Check if the response indicates failure despite 200 status
      if (response && response.success === false) {
        // Handle as error even though it's a 200 response
        dispatch(
          toast({
            type: "error",
            body:
              response.message || "Failed to submit order. Please try again.",
            visible: true,
          })
        );
        setIsSubmitting(false);
        return;
      }

      // Handle actual success
      setIsSubmitting(false);
      setOrderSubmitted(true);
    },
    (error) => {
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit order. Please try again.";

      dispatch(
        toast({
          type: "error",
          body: errorMessage,
          visible: true,
        })
      );
      setIsSubmitting(false);
    }
  );

  // Update trading allocation mutation (for active accounts)
  const updateTradingAllocationMutation = useTradingAllocation(
    (response) => {
      // Check if the response indicates failure despite 200 status
      if (response && response.success === false) {
        // Handle as error even though it's a 200 response
        dispatch(
          toast({
            type: "error",
            body:
              response.message || "Failed to submit order. Please try again.",
            visible: true,
          })
        );
        setIsSubmitting(false);
        return;
      }

      // Handle actual success
      setIsSubmitting(false);
      setOrderSubmitted(true);
    },
    (error) => {
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit order. Please try again.";

      dispatch(
        toast({
          type: "error",
          body: errorMessage,
          visible: true,
        })
      );
      setIsSubmitting(false);
    }
  );

  // Computed values
  const availableCash =
    tradingConnectionData?.available_funds_for_trading?.raw_number;
  const availableCashFormatted =
    tradingConnectionData?.available_funds_for_trading?.formatted;
  const accountValue = tradingConnectionData?.account_value?.raw_number;
  const strategy = strategyData?.strategy_name || "Strategy";

  const additionalCash =
    investmentAmount && !isNaN(investmentAmount)
      ? (parseFloat(investmentAmount) - availableCash).toFixed(2)
      : 0;

  const showAdditionalCashAlert =
    activeTab === TRADE_SIDE.BUY &&
    selectedAccount &&
    investmentAmount &&
    parseFloat(investmentAmount) > availableCash;

  const isAmountValid =
    investmentAmount &&
    !isNaN(investmentAmount) &&
    parseFloat(investmentAmount) > 0;

  // Event handlers
  const handleNext = () => {
    if (step === 1 && selectedAccount && isAmountValid) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (orderSubmitted) {
      setOrderSubmitted(false);
      setStep(1);
    }
  };

  const handleSubmitOrder = () => {
    if (!selectedAccount?.broker_account_id) {
      dispatch(
        toast({
          type: "error",
          body: "Please select an account before submitting the order.",
          visible: true,
        })
      );
      return;
    }

    trackEvent({
      name: "click_start_strategy",
      label: "Strategy",
      strategy_id: strategyId,
      strategy_name: strategyData?.strategy_name,
    });

    setIsSubmitting(true);

    // Prepare payload for update trading allocation
    const dollarAmount = parseFloat(investmentAmount);
    const dollarAllocation =
      activeTab === TRADE_SIDE.BUY ? dollarAmount : -dollarAmount;
    //   if (activeTab === "buy" && parseFloat(investmentAmount) > availableCash) {
    //           dispatch(
    //       toast({
    //         type: "error",
    //         body:  `You cannot invest more than your available cash (${availableCashFormatted}).`,
    //         visible: true,
    //       })
    //     );
    //   setIsSubmitting(false);
    //   return;
    // }

    // Check if current strategy has an existing trading connection
    const hasExistingConnection =
      tradingConnectionData?.trading_connections?.some(
        (connection) => connection.strategy_id === strategyId
      );

    // Get existing allocation if present
    const existingAllocation = tradingConnectionData?.trading_connections
      ?.filter((id) => id.strategy_id === strategyId)
      ?.reduce((sum, connection) => {
        return sum + (connection.current_allocation?.dollars?.raw_number || 0);
      }, 0);

    if (
      existingAllocation < Math.abs(dollarAllocation) &&
      activeTab === TRADE_SIDE.SELL
    ) {
      dispatch(
        toast({
          type: "error",
          body: `You can not sell more than you have invested in this strategy. You have ${existingAllocation}.`,
          visible: true,
        })
      );
      return;
    }

    // Calculate percentage allocation
    // For update case: (existing_allocation + dollarAllocation) / accountValue
    // For new connection: dollarAllocation / accountValue
    let percentageAllocation;
    if (hasExistingConnection) {
      percentageAllocation =
        (existingAllocation + dollarAllocation) / accountValue;
    } else {
      percentageAllocation = dollarAllocation / accountValue;
    }

    if (activeTab === TRADE_SIDE.SELL) {
      // Always use updateTradingAllocation for sell operations
      const updatePayload = {
        new_allocation: {
          [strategyId]: percentageAllocation,
        },
        update_flag: {
          [strategyId]: true,
        },
      };
      updateTradingAllocationMutation.mutate({
        brokerAccountId: selectedAccount.broker_account_id,
        payload: updatePayload,
      });
    } else {
      // For buy operations, check if strategy has existing connection
      if (hasExistingConnection) {
        // If strategy has existing connection, use updateTradingAllocation
        const updatePayload = {
          new_allocation: {
            [strategyId]: percentageAllocation,
          },
          update_flag: {
            [strategyId]: true,
          },
        };
        updateTradingAllocationMutation.mutate({
          brokerAccountId: selectedAccount.broker_account_id,
          payload: updatePayload,
        });
      } else {
        // If no existing connection, use postTradingConnection
        const postPayload = {
          account_id: selectedAccount.broker_account_id,
          strategy: strategyId,
          dollar_allocation: dollarAllocation,
        };
        postTradingConnectionMutation.mutate(postPayload);
      }
    }
  };

  const handleDone = () => {
    setOrderSubmitted(false);
    setStep(1);
    setInvestmentAmount("");
    setSelectedAccount("");
  };

  const handleViewDetails = () => {
    setIsHoldingsModalOpen(true);
  };

  const handleAccountSelect = (val) => {
    const account = portfolios?.find((a) => a.broker_account_id === val);

    if (account) {
      setSelectedAccount(account);

      // Call createTradingConnection on account selection
      setIsLoadingCash(true);
      createTradingConnectionMutation.mutate(account.broker_account_id);
    }
  };
  const handleSellAll = () => {
    // 1. find the connection for this strategy
    const match = tradingConnectionData?.trading_connections?.find(
      (tc) => String(tc?.strategy_id) === String(strategyId)
    );

    // 2. get the raw dollar amount
    const maxAmount = match?.current_allocation?.dollars?.raw_number;

    // 3. set the input value
    if (maxAmount !== undefined && maxAmount !== null) {
      setInvestmentAmount(maxAmount);
    }
  };

  const showSellAll = useMemo(() => {
    // 1. must be on Sell tab
    if (activeTab !== TRADE_SIDE.SELL) return false;

    // 2. must have trading data
    if (!tradingConnectionData?.trading_connections) return false;

    // 3. Find specific connection for this strategy
    const match = tradingConnectionData.trading_connections.find(
      (tc) => String(tc?.strategy_id) === String(strategyId)
    );

    // 4. must have a positive balance (> 0)
    const currentBalance = match?.current_allocation?.dollars?.raw_number || 0;
    return currentBalance > 0;
  }, [activeTab, tradingConnectionData, strategyId]);

  return {
    // State
    activeTab,
    step,
    selectedAccount,
    investmentAmount,
    isSubmitting,
    orderSubmitted,
    strategyData,
    tradingConnectionData,
    isLoadingCash,
    isHoldingsModalOpen,
    showSellAll,

    // API Data
    portfolios,
    loading,
    isError,
    holdingsData,
    isLoadingHoldings,
    isHoldingsError,

    // Computed values
    availableCash,
    availableCashFormatted,
    strategy,
    additionalCash,
    showAdditionalCashAlert,
    isAmountValid,

    // Setters
    setActiveTab,
    setInvestmentAmount,
    setIsHoldingsModalOpen,

    // Event handlers
    handleNext,
    handleBack,
    handleSubmitOrder,
    handleDone,
    handleViewDetails,
    handleAccountSelect,
    handleSellAll,
  };
};
